<script setup>

import { onMounted, ref } from 'vue'
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate"
import ProgressSpinner from 'primevue/progressspinner'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import referenceIndexService from '@/services/referenceIndex.service'
import userService from '@/services/user.service'
import rentingService from '@/services/renting.service'
import { useAccountStore } from '@/store/account.store'
import Icon from '@/components/Icon/Icon.vue'
import ReferenceIndexWidgetEditModal from '@/components/modal/ReferenceIndexWidgetEditModal.vue'
import moment from 'moment'
import 'moment/dist/locale/fr'
import { useDataStore } from '@/store/data.store'

const loadedComponent = ref(false)
const reference_indexes = ref([])
const dataStore = useDataStore()
const referenceIndexes = ref([])
const referenceIndexEditDialog = ref(false)
const referenceIndexToEdit = ref({})

const updateReferenceIndex = (data) => {
    rentingService.updateReferenceIndex(data.uuid, {
        reference_index: data.newRI.id,
        round_rent: data.roundRent,
    }).then(() => {
        reference_indexes.value.find((o, i) => {
            if(o.uuid === data.uuid) {
                reference_indexes.value.splice(i, 1)

                return true
            }
        })
    })
}

const editReferenceIndex = (data) => {
    referenceIndexToEdit.value = {...data}
    referenceIndexToEdit.value.id = data.newRI.id

    referenceIndexEditDialog.value = true
}

const editReferenceIndexEnd = (data) => {
    reference_indexes.value.find((o, i) => {
        if(o.uuid === data.uuid) {
            const found = referenceIndexes.value.find((o, i) => {
                if(o.id == data.id) {
                    return true
                }
            })

            reference_indexes.value[i].newRI = found
            reference_indexes.value[i].roundRent = data.roundRent

            return true
        }
    })
}

const cancelReferenceIndex = (data) => {
    rentingService.cancelReferenceIndex(data.uuid)
    .then(() => {
        reference_indexes.value.find((o, i) => {
            if(o.uuid === data.uuid) {
                reference_indexes.value.splice(i, 1)

                return true
            }
        })
    })
}

const getRent = (data) => {
    return (data.rent * data.newRI.irl_index / data.ri_irl_index).toFixed(2)
}

const getRoundRent = (data) => {
    return Math.round(data.rent * data.newRI.irl_index / data.ri_irl_index)
}

const isRoundRent = (data) => {
    if(data.roundRent == true) {
        return true
    }

    return false
}

onMounted(() => {
    dataStore.getReferenceIndexes()
        .then(response => referenceIndexes.value = response)

    userService.getAccounts().then((response) => {
        const accountStore = useAccountStore()
        accountStore.setAccounts(response.data.accounts)

        referenceIndexService.getToUpdate().then(data => {
            reference_indexes.value = data.data.reference_indexes

            reference_indexes.value.forEach((item) => {
                const re = /([0-9]{4})-T([1-4]{1})/.exec(item.ri_period)

                const found = referenceIndexes.value.find((o, i) => {
                    if(o.period.includes(re[2])) {
                        return true
                    }
                })

                item.newRI = found

                item.roundRent = true
            })

            loadedComponent.value = true
        })
    })
})

</script>

<template>

<div class="col-span-2 flex flex-col border border-slate-200 p-4">
    <ReferenceIndexWidgetEditModal
        :externVisible="referenceIndexEditDialog"
        :externReferenceIndex="referenceIndexToEdit"
        @edit="editReferenceIndexEnd"
        @close="referenceIndexEditDialog = false"
    />

    <div v-if="loadedComponent">
        <h3 class="m-auto text-2xl p-2">Indices de référence</h3>
        <div class="mt-2 line">
            <DataTable
                :value="reference_indexes"
                size="small"
                removableSort
                tableStyle="min-width: 40rem"
            >
                <template #empty>
                    <span class="flex justify-center w-full p-2">
                        Aucun indice de référence
                    </span>
                </template>

                <Column field="p_name" header="Bien" sortable></Column>
                <Column field="name" header="Nom" sortable></Column>
                <Column header="Début" sortable>
                    <template #body="slotProps">
                        {{ moment(slotProps.data.started_at).format('D MMM') }}
                    </template>
                </Column>
                <Column field="ri_period" header="Période"></Column>
                <Column header="Nouvelle Période">
                    <template #body="slotProps">
                        {{ slotProps.data.newRI.period }} ({{ slotProps.data.newRI.irl_index }})
                    </template>
                </Column>
                <Column header="Calcul">
                    <template #body="slotProps">
                        {{ slotProps.data.rent }} x {{ slotProps.data.newRI.irl_index }} / {{ slotProps.data.ri_irl_index }}
                        <br />
                        (Ancien loyer x Nouvel indice / Ancien indice)
                    </template>
                </Column>
                <Column header="Loyer">
                    <template #body="slotProps">
                        <span :class="isRoundRent(slotProps.data) ? '' : 'font-bold'">{{ getRent(slotProps.data) }}&nbsp;€</span>
                        <br />
                        <span :class="isRoundRent(slotProps.data) ? 'font-bold' : ''">arrondi à {{ getRoundRent(slotProps.data) }}&nbsp;€</span>
                    </template>
                </Column>
                <Column :exportable="false" style="width: 3rem">
                    <template #body="slotProps">
                        <div class="flex gap-1">
                            <button class="rounded border border-black p-1 shadow-md hover:shadow-none hover:bg-green-500" @click="editReferenceIndex(slotProps.data)" title="Modifier l'index de référence">
                                <Icon name="edit" :size="12" color="black" class="w-4 h-4" />
                            </button>
                            <button class="rounded border border-black p-1 shadow-md hover:shadow-none hover:bg-green-500" @click="updateReferenceIndex(slotProps.data)" title="Appliquer l'index de référence">
                                <Icon name="valid" :size="12" color="black" class="w-4 h-4" />
                            </button>
                            <button class="rounded border border-black p-1 shadow-md hover:shadow-none hover:bg-red-500" @click="cancelReferenceIndex(slotProps.data)" title="Annuler l'index de référence">
                                <Icon name="cancel" :size="12" color="black" class="w-4 h-4" />
                            </button>
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>
    <div v-else class="mx-4 w-full flex justify-center">
        <ProgressSpinner
            style="width: 50px; height: 50px"
            strokeWidth="5"
            fill="transparent"
            animationDuration="2.5s"
            aria-label="Custom ProgressSpinner"
            class="m-auto"
        />
    </div>
</div>

</template>
